import {
    UpdateEstablishmentWebsiteSectionRequest,
    useEstablishmentUpdate,
    useEstablishmentWebsiteSectionUpdate,
} from '@sugg-gestion/ubidreams-react-suggpro'
import { InlineEmailFormState } from 'components/forms/establishment/inlineEmailForm'
import { WebsiteCustomizationFormState } from 'components/forms/establishment/websiteCustomizationForm'
import ExternalLinksView from 'components/views/externalLinks/externalLinksView'
import EstablishmentEditPhotosDialog from 'containers/establishment/dialogs/establishmentEditPhotosDialog'
import withAuthenticateLayout, { WithAuthenticateLayout } from 'containers/hoc/withAuthenticateLayout'
import useClipboard from 'core/services/clipboard/useClipboard'
import useRestaurantsApi from 'core/services/restaurant/useRestaurantsApi'
import { useApiErrors } from 'core/services/suggPro/useApiErrors'
import { useSnackbar } from 'notistack'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Props extends WithAuthenticateLayout {}

const ExternalLinks: React.FC<Props> = ({ establishment, restaurantOwner, ability }) => {
    const { enqueueSnackbar } = useSnackbar()
    const { t } = useTranslation()

    const { displayError } = useApiErrors()
    const { updateEstablishment: bookingModuleChange, inProgress: bookingModuleChangeInProgress } =
        useEstablishmentUpdate()
    const { updateEstablishment: bookingEmailChange, inProgress: bookingEmailChangeInProgress } =
        useEstablishmentUpdate()
    const { updateEstablishment: orderModuleChange, inProgress: orderModuleChangeInProgress } =
        useEstablishmentUpdate()
    const { updateEstablishment: orderEmailChange, inProgress: orderEmailChangeInProgress } =
        useEstablishmentUpdate()
    const { updateEstablishment: websiteChange, inProgress: websiteChangeInProgress } =
        useEstablishmentUpdate()
    const { updateEstablishmentWebsiteSection, inProgress: websiteSectionChangeInProgress } =
        useEstablishmentWebsiteSectionUpdate()
    const { menuLink, qrCodeLink, qrCodeDressedLink, slateLink } = useRestaurantsApi(
        restaurantOwner,
        establishment,
    )
    const { copyToClipboard } = useClipboard()
    const [photoEditOpen, setPhotoEditOpen] = useState(false)
    const [sectionsUpdate, setSectionsUpdate] = useState(false)

    const handleCopyLink = (link: string) => {
        copyToClipboard(link)
    }

    const handleChangeBookingModule = (bookingModule: boolean) => {
        bookingModuleChange({
            id: establishment.id,
            establishment: {
                bookingModule,
            },
        })
            .then(() => {
                enqueueSnackbar(
                    t('views.externalLinks.bookingModule.' + (bookingModule ? 'enable' : 'disable')),
                    {
                        variant: 'success',
                    },
                )
            })
            .catch((error) => displayError(error))
    }

    const handleChangeBookingEmail = (values: InlineEmailFormState) => {
        bookingEmailChange({
            id: establishment.id,
            establishment: {
                bookingEmail: values.email,
            },
        })
            .then(() => {
                enqueueSnackbar(t('views.externalLinks.bookingEmail.change'), {
                    variant: 'success',
                })
            })
            .catch((error) => displayError(error))
    }

    const handleChangeOrderEmail = (values: InlineEmailFormState) => {
        orderEmailChange({
            id: establishment.id,
            establishment: {
                orderEmail: values.email,
            },
        })
            .then(() => {
                enqueueSnackbar(t('views.externalLinks.orderEmail.change'), {
                    variant: 'success',
                })
            })
            .catch((error) => displayError(error))
    }

    const handleChangeOrderModule = (orderModule: boolean) => {
        orderModuleChange({
            id: establishment.id,
            establishment: {
                orderModule,
            },
        })
            .then(() => {
                enqueueSnackbar(
                    t('views.externalLinks.orderModule.' + (orderModule ? 'enable' : 'disable')),
                    {
                        variant: 'success',
                    },
                )
            })
            .catch((error) => displayError(error))
    }

    const handleEditPhotos = () => {
        setPhotoEditOpen(true)
    }

    const handleClose = () => {
        setPhotoEditOpen(false)
    }

    const [editCustomizationModalOpen, setEditCustomizationModalOpen] = useState(false)
    const handleEditCustomization = () => {
        setEditCustomizationModalOpen(true)
    }

    const handleModalCustomizationClose = () => {
        setEditCustomizationModalOpen(false)
    }

    const handleUpdateWebsiteCustomization = (values: WebsiteCustomizationFormState, withClose = true) => {
        setSectionsUpdate(true)
        const websiteSectionsPromise: Promise<any>[] = []
        if (
            establishment.websiteFont !== values.font ||
            establishment.websiteBackgroundColorLight !== values.backgroundColorLight ||
            establishment.websiteBackgroundColorDark !== values.backgroundColorDark
        ) {
            websiteSectionsPromise.push(
                websiteChange({
                    id: establishment.id,
                    establishment: {
                        websiteFont: values.font,
                        websiteBackgroundColorLight: values.backgroundColorLight,
                        websiteBackgroundColorDark: values.backgroundColorDark,
                    },
                }),
            )
        }

        const sectionOrder = [
            'title',
            'slate',
            'menu',
            'news',
            'about',
            'links',
            'book',
            'order',
            'photos',
            'information',
            'reviews',
            'footer',
        ]
        sectionOrder.forEach((section) => {
            const currentWebsiteSection = establishment.websiteSections?.find(
                (websiteSection) => websiteSection.section === section,
            )
            if (
                currentWebsiteSection ||
                // @ts-ignore
                values[section + 'SectionBackgroundColorEnabled'].length > 0 ||
                // @ts-ignore
                values[section + 'SectionOverlayOpacityEnabled'].length > 0 ||
                // @ts-ignore
                values[section + 'SectionTextColorEnabled'].length > 0 ||
                // @ts-ignore
                values[section + 'SectionBackgroundImage']?.file ||
                // @ts-ignore
                values[section + 'SectionHidden'] ||
                // @ts-ignore
                values[section + 'SectionSortOrder'] !== 0
            ) {
                let titleSection: UpdateEstablishmentWebsiteSectionRequest = {
                    section: section,
                    backgroundColor: null,
                    textColor: null,
                    backgroundImage: undefined,
                    overlayOpacity: null,
                    establishment: establishment.id,
                }
                let hasUpdate = false
                if (currentWebsiteSection) {
                    titleSection.id = currentWebsiteSection.id
                } else {
                    hasUpdate = true
                }

                if (
                    // @ts-ignore
                    values[section + 'SectionBackgroundColorEnabled'].length > 0 &&
                    // @ts-ignore
                    values[section + 'SectionBackgroundColor']
                ) {
                    // @ts-ignore
                    titleSection.backgroundColor = values[section + 'SectionBackgroundColor']
                } else {
                    titleSection.backgroundColor = null
                }
                if (
                    currentWebsiteSection &&
                    titleSection.backgroundColor !== currentWebsiteSection.backgroundColor
                ) {
                    hasUpdate = true
                }

                if (
                    // @ts-ignore
                    values[section + 'SectionOverlayOpacityEnabled'].length > 0 &&
                    // @ts-ignore
                    values[section + 'SectionOverlayOpacity'] !== undefined
                ) {
                    // @ts-ignore
                    titleSection.overlayOpacity = values[section + 'SectionOverlayOpacity']
                } else {
                    titleSection.overlayOpacity = null
                }
                if (
                    currentWebsiteSection &&
                    // @ts-ignore
                    titleSection.overlayOpacity !== currentWebsiteSection.overlayOpacity
                ) {
                    hasUpdate = true
                }

                if (
                    // @ts-ignore
                    values[section + 'SectionTextColorEnabled'].length > 0 &&
                    // @ts-ignore
                    values[section + 'SectionTextColor']
                ) {
                    // @ts-ignore
                    titleSection.textColor = values[section + 'SectionTextColor']
                } else {
                    titleSection.textColor = null
                }
                if (currentWebsiteSection && titleSection.textColor !== currentWebsiteSection.textColor) {
                    hasUpdate = true
                }

                // @ts-ignore
                if (values[section + 'SectionBackgroundImage']) {
                    // @ts-ignore
                    if (values[section + 'SectionBackgroundImage']?.file) {
                        // @ts-ignore
                        titleSection.backgroundImage = values[section + 'SectionBackgroundImage'].file
                        hasUpdate = true
                    } else if (
                        currentWebsiteSection &&
                        // @ts-ignore
                        values[section + 'SectionBackgroundImage']?.url === undefined
                    ) {
                        titleSection.backgroundImage = null
                        hasUpdate = true
                    }
                } else {
                    titleSection.backgroundImage = null
                    if (currentWebsiteSection && currentWebsiteSection.backgroundImage !== null) {
                        hasUpdate = true
                    }
                }

                // @ts-ignore
                titleSection.hidden = values[section + 'SectionHidden']
                if (currentWebsiteSection && titleSection.hidden !== currentWebsiteSection.hidden) {
                    hasUpdate = true
                }

                // @ts-ignore
                titleSection.sortOrder = values[section + 'SectionSortOrder']
                if (currentWebsiteSection && titleSection.sortOrder !== currentWebsiteSection.sortOrder) {
                    hasUpdate = true
                }

                if (hasUpdate) {
                    websiteSectionsPromise.push(updateEstablishmentWebsiteSection(titleSection))
                }
            }
        })

        Promise.all(websiteSectionsPromise)
            .then(() => {
                enqueueSnackbar(t('views.externalLinks.customization.change'), {
                    variant: 'success',
                })
                if (withClose) {
                    handleModalCustomizationClose()
                }
            })
            .catch((error) => displayError(error))
            .finally(() => {
                setSectionsUpdate(false)
            })
    }

    return (
        <>
            <ExternalLinksView
                establishment={establishment}
                slateLink={slateLink()}
                menuLink={menuLink()}
                copyLink={handleCopyLink}
                qrCodeLink={qrCodeLink()}
                qrCodeDressedLink={qrCodeDressedLink()}
                bookingModuleAccess={ability.can('api.booking_access')}
                bookingModule={establishment.bookingModule}
                bookingModuleChangeInProgress={bookingModuleChangeInProgress}
                bookingEmail={establishment.bookingEmail ?? establishment.email ?? ''}
                bookingEmailChangeInProgress={bookingEmailChangeInProgress}
                onChangeBookingEmail={handleChangeBookingEmail}
                onChangeBookingModule={handleChangeBookingModule}
                orderModuleAccess={ability.can('api.order_access')}
                orderModule={establishment.orderModule}
                orderModuleChangeInProgress={orderModuleChangeInProgress}
                onChangeOrderModule={handleChangeOrderModule}
                enableOrderModule
                orderEmail={establishment.orderEmail ?? establishment.email ?? ''}
                orderEmailChangeInProgress={orderEmailChangeInProgress}
                onChangeOrderEmail={handleChangeOrderEmail}
                onEditPhotos={handleEditPhotos}
                editCustomizationModalOpen={editCustomizationModalOpen}
                onEditCustomization={handleEditCustomization}
                onCloseCustomization={handleModalCustomizationClose}
                onUpdateWebsiteCustomization={handleUpdateWebsiteCustomization}
                updateWebsiteCustomizationInProgress={
                    websiteChangeInProgress || websiteSectionChangeInProgress || sectionsUpdate
                }
            />

            <EstablishmentEditPhotosDialog
                establishment={establishment}
                open={photoEditOpen}
                onClose={handleClose}
            />
        </>
    )
}

export default withAuthenticateLayout(ExternalLinks)
